import { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';

/**
 * Tells people when the tab they are looking at is out of date.
 *
 * A single-page app keeps running whatever JavaScript it downloaded, for days
 * if the tab stays open. That has been the quiet cause of several "it works
 * for him but not for me" reports: half a meeting on one build and half on
 * another, behaving differently and with nothing on screen to explain it.
 *
 * The check needs no build plumbing. Vite fingerprints the entry script, so
 * the page already knows which build it is running; asking the server for the
 * current index.html and comparing that filename answers the question exactly.
 *
 * It never reloads on its own. Doing that to somebody mid-sentence in a
 * meeting would be worse than the staleness.
 */

const CHECK_INTERVAL_MS = 10 * 60_000;
const ENTRY_PATTERN = /\/assets\/[^"']*index-[A-Za-z0-9_-]+\.js/;

function runningBundle(): string | null {
  for (const script of document.querySelectorAll('script[type="module"][src]')) {
    const src = script.getAttribute('src') ?? '';
    const match = ENTRY_PATTERN.exec(src);
    if (match) return match[0];
  }
  // A dev server serves unhashed modules, so there is nothing to compare.
  return null;
}

async function deployedBundle(): Promise<string | null> {
  try {
    const response = await fetch('/index.html', { cache: 'no-store' });
    if (!response.ok) return null;
    return ENTRY_PATTERN.exec(await response.text())?.[0] ?? null;
  } catch {
    // Offline, or the server is restarting mid-deploy. Ask again later.
    return null;
  }
}

export function UpdateBanner() {
  const [stale, setStale] = useState(false);

  const check = useCallback(async () => {
    const running = runningBundle();
    if (!running) return;
    const deployed = await deployedBundle();
    if (deployed && deployed !== running) setStale(true);
  }, []);

  useEffect(() => {
    // On returning to the tab, because that is when somebody is about to use
    // it, and on a timer for a tab left open all day.
    const onFocus = (): void => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    const timer = window.setInterval(() => void check(), CHECK_INTERVAL_MS);
    void check();
    return () => {
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
      window.clearInterval(timer);
    };
  }, [check]);

  if (!stale) return null;

  return (
    <div
      className="glass-strong fixed inset-x-0 top-3 z-[60] mx-auto flex w-[min(30rem,calc(100vw-1.5rem))] items-center gap-3 rounded-full px-4 py-2.5 shadow-glass-lg"
      role="status"
    >
      <RefreshCw className="h-4 w-4 shrink-0 text-brand-600" aria-hidden />
      <p className="min-w-0 flex-1 text-sm text-slate-700">
        <span className="font-bold text-slate-900">HomeWork has been updated.</span> Reload when you
        get a moment — this tab is running an older version.
      </p>
      <button type="button" className="btn-primary shrink-0 px-3 py-1.5 text-xs" onClick={() => window.location.reload()}>
        Reload
      </button>
    </div>
  );
}
